import {QueryClient, UseQueryResult, useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import IWBrokerClient from '../client/IWBrokerClient';
import {gatewayRealtimeQueryOptions, gatewayUserScopedQueryOptions} from './gatewayQueryUtils';

export type EVCharger = Record<string, any> & {id: string};
export type EVChargers = Record<string, EVCharger>;

export const evChargersQueryKey = ['ev charger list'] as const;
export const evChargeHistoryQueryKey = ['ev charge history'] as const;
const EV_CHARGE_HISTORY_PAGE_SIZE = 20;

export type EVSession = {
    id: number;
    userId: number | null;
    userName: string | null;
    chargeStationId: string;
    chargeStationName: string;
    connectorId: number;
    timestampStart: string;
    timestampStop: string | null;
    meterStart: number;
    meterStop: number | null;
    energyKwh: number | string | null;
    durationSeconds: number | null;
};

const installedEVChargersListeners = new WeakSet<QueryClient>();

function installEVChargersListener(queryClient: QueryClient) {
    if (installedEVChargersListeners.has(queryClient)) {
        return;
    }

    IWBrokerClient.socket.on('ev charger update', (charger: EVCharger) => {
        console.log('(evChargersQuery) ev charger update', charger);
        queryClient.setQueryData<EVChargers>(evChargersQueryKey, (current: EVChargers | undefined) => ({
            ...current,
            [charger.id]: charger
        }));

        const completedSession = Object.values(charger.connectors ?? {})
            .some((connector: any) => connector.transaction?.timestampStop);
        if (completedSession) {
            queryClient.invalidateQueries({queryKey: evChargeHistoryQueryKey});
        }
    });

    installedEVChargersListeners.add(queryClient);
}

export function useEVChargeHistoryQuery(enabled = true) {
    return useInfiniteQuery({
        queryKey: evChargeHistoryQueryKey,
        queryFn: async ({pageParam: idBefore}) => {
            return IWBrokerClient.request('ev transaction history', {idBefore}) as Promise<EVSession[]>;
        },
        ...gatewayUserScopedQueryOptions,
        staleTime: 1000 * 60,
        initialPageParam: undefined as number | undefined,
        getNextPageParam: (lastPage: EVSession[]) => {
            return lastPage.length === EV_CHARGE_HISTORY_PAGE_SIZE
                ? lastPage[lastPage.length - 1].id
                : undefined;
        },
        enabled
    });
}

export function useEVChargersQuery(): UseQueryResult<EVChargers, Error> {
    const queryClient = useQueryClient();
    installEVChargersListener(queryClient);

    return useQuery<EVChargers>({
        queryKey: evChargersQueryKey,
        queryFn: async () => IWBrokerClient.request('ev charger list') as Promise<EVChargers>,
        ...gatewayRealtimeQueryOptions
    });
}

export interface EVChargerRequest {
    chargePointId: string;
    [key: string]: any;
}

export function useEVChargerRequestMutation() {
    return useMutation({
        mutationKey: ['ev charger request'],
        mutationFn: async (request: EVChargerRequest) => {
            return IWBrokerClient.request('ev charger request', request);
        }
    });
}
